import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SearchBar from '../components/SearchBar';
import BusinessCard from '../components/BusinessCard';
import { getFeatured, getCategories, getTopics, getStats } from '../api';
import {
  Utensils, Building2, Heart, ShoppingBag, Wrench, GraduationCap,
  Stethoscope, Home, Camera, Briefcase, Leaf, Car, ArrowRight, Star,
  Sparkles, ChevronRight
} from 'lucide-react';

const CATEGORY_ICONS = {
  'ristorante': Utensils, 'pizzeria': Utensils, 'pub': Utensils,
  'farmacia': Heart, 'medico': Stethoscope, 'chirurgo': Stethoscope,
  'poliambulatorio': Stethoscope, 'studio': Briefcase,
  'negozio': ShoppingBag, 'abbigliamento': ShoppingBag, 'profumeria': ShoppingBag,
  'cantina': Leaf, 'vivaio': Leaf,
  'ingegneria': Wrench, 'impiantistica': Wrench, 'edilizia': Building2,
  'scuola': GraduationCap, 'università': GraduationCap, 'formazione': GraduationCap,
  'hotel': Home, 'albergo': Home, 'location': Home, 'sala': Home,
  'auto': Car, 'officina': Car, 'noleggio': Car,
};

function getCategoryIcon(cat) {
  if (!cat) return Building2;
  const lower = cat.toLowerCase();
  for (const [key, Icon] of Object.entries(CATEGORY_ICONS)) {
    if (lower.includes(key)) return Icon;
  }
  return Building2;
}

function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!target || startedRef.current) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          let start = 0;
          const step = target / (duration / 16);
          const tick = () => {
            start += step;
            if (start >= target) { setCount(target); return; }
            setCount(Math.floor(start));
            requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return [count, ref];
}

function StatCounter({ value, label, suffix = '+' }) {
  const [count, ref] = useCountUp(value);
  return (
    <div ref={ref} className="text-center">
      <p className="font-display text-4xl md:text-5xl font-bold" style={{ color: '#fff' }}>
        {count.toLocaleString('it-IT')}{suffix}
      </p>
      <p className="text-xs uppercase tracking-widest mt-1 font-medium" style={{ color: 'rgba(255,255,255,0.55)' }}>{label}</p>
    </div>
  );
}

export default function HomePage() {
  const [featured, setFeatured]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [topics, setTopics]       = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, catRes, topRes, statRes] = await Promise.all([
          getFeatured(8),
          getCategories(),
          getTopics(20),
          getStats(),
        ]);
        setFeatured(featRes.data);
        setCategories(catRes.data);
        setTopics(topRes.data);
        setStats(statRes.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div style={{ backgroundColor: 'var(--mi-bg)' }}>
      <Header />

      {/* ── HERO ─────────────────────────────────────────── */}
      <section
        data-testid="home-hero"
        className="mi-noise relative overflow-hidden"
        style={{
          background: 'linear-gradient(150deg, #FAFAF8 0%, #F3F1EC 55%, #FAFAF8 100%)',
          paddingTop: '5rem',
          paddingBottom: '5rem',
        }}
      >
        {/* Radial glow top-right */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-80px', right: '-80px',
            width: '480px', height: '480px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(200,169,110,0.18) 0%, transparent 70%)',
          }}
        />

        <div className="mi-container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">

            {/* Text */}
            <div className="lg:col-span-5 relative z-10">
              <span
                className="animate-fade-in-up inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.2em] font-semibold mb-6 px-4 py-2 rounded-full border"
                style={{ borderColor: 'var(--mi-accent)', color: 'var(--mi-accent)', backgroundColor: 'var(--mi-accent-light)', backdropFilter: 'blur(8px)' }}
              >
                <Sparkles size={12} />
                Cosa Mi-Ispira oggi?
              </span>
              <h1
                className="animate-fade-in-up-delay-1 font-display leading-[0.95] tracking-tight mb-6"
                style={{
                  color: '#1A1A1A',
                  fontSize: 'clamp(3rem, 7vw, 5.5rem)',
                  textShadow: '0 4px 12px rgba(26,26,26,0.05)',
                }}
              >
                Scopri il meglio
                <em
                  className="block not-italic mt-1"
                  style={{ color: 'var(--mi-accent)', fontStyle: 'italic' }}
                >
                  vicino a te.
                </em>
              </h1>
              <p
                className="animate-fade-in-up-delay-2 text-base md:text-xl leading-[1.6] mb-10 max-w-[32ch]"
                style={{ color: '#6B6860' }}
              >
                Attività locali, professionisti e realtà del territorio curate e verificate per te.
              </p>

              <div className="animate-fade-in-up-delay-3 flex flex-wrap items-center gap-4">
                <Link
                  to="/scopri"
                  className="btn-gold inline-flex items-center justify-center gap-2 px-8 h-14 rounded-full text-sm font-bold transition-all hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(200,169,110,0.3)]"
                >
                  <Sparkles size={16} />
                  Inizia a scoprire
                </Link>
                <Link
                  to="/attivita"
                  className="group inline-flex items-center justify-center gap-2 px-8 h-14 rounded-full text-sm font-semibold border transition-all hover:bg-white hover:border-[var(--mi-accent)] hover:shadow-sm"
                  style={{ borderColor: 'rgba(26,26,26,0.12)', color: '#1A1A1A' }}
                >
                  Sfoglia tutto <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>

            {/* Search box */}
            <div className="lg:col-span-7 animate-fade-in-up-delay-2 relative z-10">
              <div
                className="p-8 md:p-10 rounded-[2rem] border transition-transform duration-500 hover:shadow-[0_24px_80px_rgba(200,169,110,0.12)]"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.85)',
                  borderColor: 'rgba(26,26,26,0.06)',
                  boxShadow: '0 12px 48px rgba(200,169,110,0.08), 0 2px 12px rgba(26,26,26,0.04)',
                  backdropFilter: 'blur(20px)',
                }}
              >
                <p className="text-[11px] uppercase tracking-[0.15em] mb-4 font-bold" style={{ color: '#9B9590' }}>
                  Cosa stai cercando?
                </p>
                <SearchBar />
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {['Ristorante', 'Farmacia', 'Estetica', 'Avvocato', 'Piscine'].map((hint) => (
                    <button
                      key={hint}
                      onClick={() => navigate(`/attivita?q=${encodeURIComponent(hint)}`)}
                      className="text-xs px-4 py-2 rounded-full border font-medium transition-all hover:-translate-y-0.5 hover:border-[var(--mi-accent)] hover:bg-white hover:shadow-sm"
                      style={{ borderColor: 'rgba(26,26,26,0.1)', color: '#6B6860', backgroundColor: 'rgba(255,255,255,0.4)' }}
                    >
                      {hint}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATISTICS BAR ───────────────────────────────── */}
      {stats && (
        <section style={{ backgroundColor: 'var(--mi-fg)', padding: '2.5rem 0' }}>
          <div className="mi-container">
            <div className="flex flex-wrap md:flex-nowrap items-center justify-between gap-6 md:gap-0">
              <StatCounter value={stats.total_businesses} label="Attività" />
              <div className="hidden md:block w-px h-10" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
              <StatCounter value={stats.categories} label="Categorie" />
              <div className="hidden md:block w-px h-10" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
              <StatCounter value={48} label="Province" />
              <div className="hidden md:block w-px h-10" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }} />
              <StatCounter value={20} label="Regioni" />
            </div>
          </div>
          <style>{`
            section[style*="1A1A1A"] .font-display { color: #FAFAF8 !important; }
            section[style*="1A1A1A"] p:last-child { color: #9A9490 !important; }
          `}</style>
        </section>
      )}

      {/* ── FEATURED ─────────────────────────────────────── */}
      {(featured.length > 0 || loading) && (
        <section className="mi-section">
          <div className="mi-container">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] mb-3 font-bold" style={{ color: 'var(--mi-accent)' }}>
                  Selezionate per te
                </p>
                <h2 className="font-display text-4xl md:text-5xl" style={{ color: '#1A1A1A', letterSpacing: '-0.02em' }} data-testid="featured-section">
                  In Evidenza
                </h2>
              </div>
              <Link
                to="/attivita?featured=true"
                className="group flex items-center gap-2 text-sm font-semibold transition-colors hover:text-[var(--mi-accent)]"
                style={{ color: '#6B6860' }}
              >
                Vedi tutte <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--mi-border)' }}>
                    <div className="skeleton-shimmer" style={{ paddingTop: '56.25%' }} />
                    <div className="p-4 space-y-2">
                      <div className="skeleton-shimmer h-5 rounded w-3/4" />
                      <div className="skeleton-shimmer h-3 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {featured.slice(0, 8).map((b) => (
                  <BusinessCard key={b._id} business={b} />
                ))}

                {/* Marketing Banner */}
                <div
                  data-testid="marketing-banner-cta"
                  className="rounded-2xl border-2 overflow-hidden flex flex-col items-center justify-center p-6 transition-all group cursor-pointer hover:shadow-xl"
                  style={{
                    borderColor: 'var(--mi-accent)',
                    backgroundColor: '#fff',
                    minHeight: '280px',
                  }}
                  onClick={() =>
                    window.open(
                      'mailto:info@mi-ispira.it?subject=Voglio entrare in Mi-Ispira&body=Salve,%0D%0A%0D%0AVorrei ricevere informazioni su come inserire la mia attività nella directory Mi-Ispira.%0D%0A%0D%0AGrazie',
                      '_blank'
                    )
                  }
                >
                  <div className="text-center space-y-3">
                    <div className="text-4xl mb-2" style={{ color: 'var(--mi-accent)' }}>✦</div>
                    <h3
                      className="font-display text-xl md:text-2xl leading-tight tracking-wide font-bold"
                      style={{ color: 'var(--mi-accent)' }}
                    >
                      Vuoi far parte<br />di Mi-Ispira?
                    </h3>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--mi-text)' }}>
                      Fai crescere la tua attività entrando nella directory
                    </p>
                    <div
                      className="mt-4 btn-gold inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-medium"
                    >
                      Contattaci ora <ArrowRight size={14} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── SCOPRI CTA ───────────────────────────────────── */}
      <section
        className="mi-noise relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #121212 0%, #1F1F1F 100%)',
          padding: '6rem 0',
        }}
      >
        <div 
          className="absolute inset-0 pointer-events-none opacity-30" 
          style={{
            background: 'radial-gradient(circle at center, rgba(200,169,110,0.4) 0%, transparent 60%)'
          }}
        />
        <div className="mi-container relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="text-center md:text-left md:max-w-2xl">
              <p className="text-[11px] uppercase tracking-[0.2em] mb-4 font-bold" style={{ color: 'var(--mi-accent)' }}>
                Nuova funzione interattiva
              </p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mb-6" style={{ color: '#FAFAF8', letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                Lasciati Ispirare
              </h2>
              <p className="text-base md:text-lg leading-[1.6]" style={{ color: '#A3A3A3' }}>
                Un nuovo modo per esplorare il territorio. Scorri le attività come in un mazzo di carte, salva quelle che ti piacciono e salta le altre.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/scopri"
                className="btn-gold inline-flex items-center justify-center gap-3 px-10 h-16 rounded-full text-base font-bold transition-all hover:scale-[1.03] hover:shadow-[0_12px_36px_rgba(200,169,110,0.4)]"
              >
                <Sparkles size={18} />
                Inizia a scoprire
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── CATEGORIES ───────────────────────────────────── */}
      <section className="mi-section" style={{ backgroundColor: 'var(--mi-muted)' }}>
        <div className="mi-container">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: 'var(--mi-accent)' }}>
              Sfoglia per settore
            </p>
            <h2 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--mi-fg)' }}>Categorie</h2>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                <div key={i} className="skeleton-shimmer h-24 rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {categories.slice(0, 20).map((cat, idx) => {
                const Icon = getCategoryIcon(cat.nome);
                return (
                  <Link
                    key={`cat-${cat.slug}-${idx}`}
                    to={`/categoria/${cat.slug}`}
                    className="group flex flex-col items-center gap-2 p-4 rounded-2xl border bg-white text-center transition-all hover:border-[var(--mi-accent)] hover:shadow-md hover:-translate-y-0.5"
                    style={{ borderColor: 'var(--mi-border)', transition: 'all 0.2s ease' }}
                  >
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors group-hover:bg-[var(--mi-accent)]"
                      style={{ backgroundColor: 'var(--mi-muted)' }}
                    >
                      <Icon size={18} className="transition-colors group-hover:text-white" style={{ color: 'var(--mi-fg)' }} />
                    </div>
                    <p className="text-xs font-semibold leading-tight" style={{ color: 'var(--mi-fg)' }}>{cat.nome}</p>
                    <p className="text-xs" style={{ color: 'var(--mi-accent)' }}>{cat.count}</p>
                  </Link>
                );
              })}
            </div>
          )}
          <div className="text-center mt-8">
            <Link
              to="/attivita"
              className="inline-flex items-center gap-2 px-6 h-11 rounded-xl text-sm font-medium border transition-all hover:bg-white hover:border-[var(--mi-accent)] hover:shadow-sm"
              style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)' }}
            >
              Esplora tutte le categorie <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TOPICS ───────────────────────────────────────── */}
      {topics.length > 0 && (
        <section className="mi-section">
          <div className="mi-container">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: 'var(--mi-accent)' }}>
                Esplora per tema
              </p>
              <h2 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--mi-fg)' }}>Argomenti</h2>
            </div>
            <div className="flex flex-wrap gap-2.5 justify-center">
              {topics.map((t, idx) => (
                <Link
                  key={`topic-${t.slug}-${idx}`}
                  to={`/argomento/${t.slug}`}
                  className="px-4 py-2 rounded-full border text-sm font-medium transition-all hover:border-[var(--mi-accent)] hover:bg-[var(--mi-accent-light)] hover:text-[var(--mi-fg)]"
                  style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-text)', backgroundColor: '#fff' }}
                >
                  {t.nome}
                  <span className="ml-2 text-xs font-bold" style={{ color: 'var(--mi-accent)' }}>{t.count}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA DARK ─────────────────────────────────────── */}
      <section
        className="mi-section mi-noise"
        style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2C 100%)' }}
      >
        <div className="mi-container text-center relative z-10">
          <Star size={24} className="mx-auto mb-4" style={{ color: 'var(--mi-accent)' }} />
          <h2 className="font-display text-3xl md:text-4xl mb-4" style={{ color: '#FAFAF8' }}>
            La tua attività non è presente?
          </h2>
          <p className="text-base mb-8" style={{ color: '#9A9490' }}>
            Contatta il nostro team per inserire la tua realtà nella directory.
          </p>
          <a
            href="mailto:info@mi-ispira.it?subject=Voglio inserire la mia attività&body=Salve,%0D%0A%0D%0AVorrei ricevere informazioni su come inserire la mia attività nella directory Mi-Ispira.%0D%0A%0D%0AGrazie"
            className="btn-gold inline-flex items-center gap-2 px-8 h-12 rounded-xl text-sm font-medium"
          >
            Contattaci ora <ArrowRight size={14} />
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
